import { useId, useState, type ReactNode } from 'react';
import { median } from '../lib/format';
import { finite, shortDay } from './charts';
import { ICON_NAMES, Icon, type IconName } from './Icon';

/* The glass chart kit, rebuilt from the Higgsfield references in
   design/glass-refs/. The material is frosted glass (tracks, tooltips, legends,
   the plot bed); the marks stay solid and keep their identity colour, with a
   gloss that only ever lightens it. A glossy gradient that shifted hue would
   stop a tracker's colour meaning that tracker. */

type Fmt = (v: number | null | undefined) => string;

/* ------------------------------------------------------------ smoothing
   Monotone cubic (Fritsch–Carlson). The curve passes through every point and
   never overshoots between them, so smoothing cannot invent a peak or a dip
   the data does not have. */
function monotonePath(pts: [number, number][]): string {
  const n = pts.length;
  if (n === 0) return '';
  if (n < 3) return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const dx: number[] = []; const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i] as [number, number]; const [x1, y1] = pts[i + 1] as [number, number];
    dx.push(x1 - x0); m.push((y1 - y0) / (x1 - x0 || 1));
  }
  const t: number[] = [m[0] as number];
  for (let i = 1; i < n - 1; i++) {
    const a = m[i - 1] as number, b = m[i] as number;
    t.push(a * b <= 0 ? 0 : (3 * ((dx[i - 1] as number) + (dx[i] as number))) / (((2 * (dx[i] as number) + (dx[i - 1] as number)) / a) + (((dx[i] as number) + 2 * (dx[i - 1] as number)) / b)));
  }
  t.push(m[n - 2] as number);
  let d = `M${(pts[0] as [number, number])[0].toFixed(1)},${(pts[0] as [number, number])[1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = pts[i] as [number, number]; const [x1, y1] = pts[i + 1] as [number, number];
    const h = (dx[i] as number) / 3;
    d += ` C${(x0 + h).toFixed(1)},${(y0 + (t[i] as number) * h).toFixed(1)} ${(x1 - h).toFixed(1)},${(y1 - (t[i + 1] as number) * h).toFixed(1)} ${x1.toFixed(1)},${y1.toFixed(1)}`;
  }
  return d;
}

/* ----------------------------------------------------------- icon badge */

// Badge names used across the cards, mapped onto the glass icon set.
const ICONS: Record<string, IconName> = {
  money: 'coins', gap: 'gap', flow: 'flow', wallet: 'wallets', chain: 'chains', star: 'star',
  rank: 'trophy', table: 'grid', size: 'steps', line: 'trend', window: 'clock',
};

export function IconBadge({ icon, tone = 'blue' }: { icon: keyof typeof ICONS | string; tone?: 'blue' | 'violet' | 'magenta' | 'amber' | 'teal' }) {
  return (
    <span className={`icon-badge ib-${tone}`} aria-hidden>
      <Icon name={ICONS[icon] ?? ((ICON_NAMES as readonly string[]).includes(icon) ? icon as IconName : 'coins')} size={24} />
    </span>
  );
}

/** The heading every glass card uses: a badge, a title, a note, and an optional control. */
export function CardHead({ icon, tone, title, note, children }: { icon: string; tone?: 'blue' | 'violet' | 'magenta' | 'amber' | 'teal'; title: string; note?: string; children?: ReactNode }) {
  return (
    <div className="gcard-head">
      <IconBadge icon={icon} {...(tone ? { tone } : {})} />
      <div className="gcard-title"><h2>{title}</h2>{note && <p>{note}</p>}</div>
      {children && <div className="gcard-tools">{children}</div>}
    </div>
  );
}

/* ------------------------------------------------------------ sparkline */

export function Spark({ values, color = 'var(--blue)', w = 120, h = 40 }: { values: number[]; color?: string; w?: number; h?: number }) {
  const uid = useId().replace(/:/g, '');
  const v = values.filter(finite);
  if (v.length < 2) return null;
  const lo = Math.min(...v), hi = Math.max(...v), span = hi - lo || 1;
  const pts = v.map((y, i) => [(i / (v.length - 1)) * w, h - 4 - ((y - lo) / span) * (h - 8)] as [number, number]);
  const d = monotonePath(pts);
  return (
    <svg className="gspark" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden>
      <defs>
        <linearGradient id={`${uid}-a`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" style={{ stopColor: color, stopOpacity: 0.32 }} />
          <stop offset="1" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      <path d={`${d} L${w},${h} L0,${h} Z`} fill={`url(#${uid}-a)`} />
      <path d={d} fill="none" style={{ stroke: color }} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/* ------------------------------------------------------------- KPI card */

export function Kpi({ icon, tone, label, value, sub, spark, sparkColor }: {
  icon: string; tone?: 'blue' | 'violet' | 'magenta' | 'amber' | 'teal'; label: string; value: ReactNode; sub?: ReactNode; spark?: number[]; sparkColor?: string;
}) {
  // Label on one line beside the badge, the figure on its own line, and the
  // sparkline sharing the last line with the note, so it never crowds the figure.
  return (
    <div className="gkpi">
      <IconBadge icon={icon} {...(tone ? { tone } : {})} />
      <small className="gkpi-label">{label}</small>
      <b className="gkpi-value">{value}</b>
      <div className="gkpi-foot">
        {sub && <span>{sub}</span>}
        {spark && spark.length > 1 && <Spark values={spark} w={84} h={28} {...(sparkColor ? { color: sparkColor } : {})} />}
      </div>
    </div>
  );
}

/* ----------------------------------------------------- vertical bars */

/** One reading per day, each bar in its own frosted track, median marked with a glass label. */
export function GlassBars({ points, format, color, height = 190 }: {
  points: { day: string; value: number | null }[]; format: Fmt; color?: string; height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const vals = points.map((p) => p.value).filter(finite);
  if (vals.length < 2) return <p className="empty">Not enough published days.</p>;
  // Headroom above the tallest bar holds the tooltip, so bars, median and
  // tooltip all share one scale instead of a padded box that would misalign them.
  const top = Math.max(...vals) * 1.25;
  const mid = median(vals);
  const at = hover ?? points.length - 1;
  const active = points[at];
  const tipLeft = ((at + 0.5) / points.length) * 100;

  return (
    <div className="gbars" style={{ ['--gb-color' as string]: color ?? 'var(--blue)' }}>
      <div className="gbars-plot" style={{ height }} onMouseLeave={() => setHover(null)}>
        {mid !== null && (
          <div className="gbars-mid" style={{ bottom: `${(mid / top) * 100}%` }}>
            <span>Median {format(mid)}</span>
          </div>
        )}
        {active && finite(active.value) && (() => {
          // Above the bar when it fits inside the plot; otherwise beside it, so a
          // tall latest bar never pushes the tooltip up over the card's controls.
          const TIP = 54;
          const barTop = (active.value / top) * height;
          const above = barTop + 14 + TIP <= height;
          const style: React.CSSProperties = above
            ? { left: `clamp(60px, ${tipLeft}%, calc(100% - 60px))`, bottom: barTop + 14 }
            : { left: `${tipLeft}%`, bottom: Math.max(0, Math.min(height - TIP, barTop - TIP / 2)), transform: at > points.length / 2 ? 'translateX(calc(-100% - 16px))' : 'translateX(16px)' };
          return (
          <div className="gtip" style={style}>
            <small>{shortDay(active.day)}{hover === null ? ' · latest' : ''}</small>
            <b>{format(active.value)}</b>
          </div>
          );
        })()}
        {points.map((p, i) => (
          <span key={p.day} className={`gbar${i === at ? ' on' : ''}${finite(p.value) ? '' : ' miss'}`} tabIndex={0}
            onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
            aria-label={`${shortDay(p.day)}: ${format(p.value)}`}>
            <i style={{ height: `${finite(p.value) ? Math.max(3, (p.value / top) * 100) : 0}%`, animationDelay: `${i * 16}ms` }} />
          </span>
        ))}
      </div>
      <div className="gaxis">
        <span>{shortDay((points[0] as { day: string }).day)}</span>
        <span>{shortDay((points[Math.floor(points.length / 2)] as { day: string }).day)}</span>
        <span>{shortDay((points[points.length - 1] as { day: string }).day)}</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------- horizontal bars */

/** `color` is the series colour: it tints the bar and puts a matching dot by the label. Bars coloured by class (gap steps) get no dot, since it would only repeat the bar. */
export interface GBar { key: string; label: ReactNode; value: number; display: string; sub?: string; color?: string; className?: string; href?: string }

/** Thick glossy bars in frosted tracks: a dot and label, the bar, the value and a sub line. */
export function GlassHBars({ items, max }: { items: GBar[]; max?: number }) {
  if (!items.length) return <p className="empty">Nothing published here.</p>;
  const top = max ?? Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="ghbars">
      {items.map((it, i) => {
        const body = (
          <>
            <span className="ghbar-label">{it.color && <i className="gdot" style={{ background: it.color }} aria-hidden />}{it.label}</span>
            <span className="ghbar-track">
              <i className={it.className} style={{ width: `${Math.max(4, (it.value / top) * 100)}%`, animationDelay: `${i * 60}ms`, ...(it.color ? { ['--gb-color' as string]: it.color } : {}) }} />
            </span>
            <span className="ghbar-value"><b>{it.display}</b>{it.sub && <small>{it.sub}</small>}</span>
          </>
        );
        return it.href ? <a key={it.key} className="ghbar" href={it.href}>{body}</a> : <div key={it.key} className="ghbar">{body}</div>;
      })}
    </div>
  );
}

/* ------------------------------------------------------ grouped columns */

export function GlassColumns({ groups, series, format, height = 200 }: {
  groups: string[]; series: { key: string; name: string; color: string; values: (number | null)[] }[]; format: Fmt; height?: number;
}) {
  const all = series.flatMap((s) => s.values).filter(finite);
  if (!groups.length || !all.length) return <p className="empty">Not published.</p>;
  const top = Math.max(...all) * 1.1;
  return (
    <div className="gcols">
      <div className="glegend">
        {series.map((s) => <span key={s.key}><i className="gdot" style={{ background: s.color }} aria-hidden />{s.name}</span>)}
      </div>
      <div className="gcols-plot" style={{ height }}>
        {groups.map((g, gi) => (
          <div key={g} className="gcol">
            <div className="gcol-bars">
              {series.map((s) => {
                const v = s.values[gi];
                return (
                  <span key={s.key} className={`gcol-bar${finite(v) ? '' : ' miss'}`} title={`${s.name}, ${g}: ${finite(v) ? format(v) : 'not published'}`}>
                    <i style={{ height: `${finite(v) ? Math.max(3, (v / top) * 100) : 0}%`, ['--gb-color' as string]: s.color }} />
                  </span>
                );
              })}
            </div>
            <span className="gcol-label">{g}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- smooth lines */

export interface GSeries { key: string; name: string; color: string; points: { day: string; value: number }[] }

/** Smooth series over time with gradient washes, a crosshair, a glass tooltip and a summary strip. */
export function SmoothLines({ series, format, height = 240 }: { series: GSeries[]; format: Fmt; height?: number }) {
  const uid = useId().replace(/:/g, '');
  const [hover, setHover] = useState<string | null>(null);
  const live = series.filter((s) => s.points.length >= 2);
  const days = [...new Set(live.flatMap((s) => s.points.map((p) => p.day)))].sort();
  if (!live.length || days.length < 2) return <p className="empty">No tracker publishes a daily series for this.</p>;

  const W = 680, H = height, L = 56, R = 16, T = 16, B = 30;
  const hi = Math.max(...live.flatMap((s) => s.points.map((p) => p.value)), 1) * 1.1;
  const t0 = Date.parse(days[0] as string), t1 = Date.parse(days[days.length - 1] as string);
  const x = (d: string) => L + ((Date.parse(d) - t0) / (t1 - t0 || 1)) * (W - L - R);
  const y = (v: number) => T + (1 - v / hi) * (H - T - B);
  const active = hover ?? (days[days.length - 1] as string);
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const xTicks = [...new Set([0, Math.floor((days.length - 1) / 2), days.length - 1])].map((i) => days[i] as string);

  const move = (e: React.PointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * W;
    let best = days[0] as string;
    for (const d of days) if (Math.abs(x(d) - px) < Math.abs(x(best) - px)) best = d;
    setHover(best);
  };
  const tipX = (x(active) / W) * 100;

  return (
    <div className="glines">
      <div className="glines-plot">
        <svg viewBox={`0 0 ${W} ${H}`} onPointerMove={move} onPointerLeave={() => setHover(null)} role="img"
          aria-label={`${live.map((s) => s.name).join(', ')}, ${shortDay(days[0] as string)} to ${shortDay(days[days.length - 1] as string)}`}>
          <defs>
            <clipPath id={`${uid}-clip`}><rect className="sweep" x={L} y="0" width={W - L - R} height={H} /></clipPath>
            {live.map((s) => (
              <linearGradient key={s.key} id={`${uid}-${s.key}`} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" style={{ stopColor: s.color, stopOpacity: live.length === 1 ? 0.34 : 0.22 }} />
                <stop offset="1" style={{ stopColor: s.color, stopOpacity: 0 }} />
              </linearGradient>
            ))}
          </defs>
          {ticks.map((f) => (
            <g key={f}>
              <line x1={L} x2={W - R} y1={y(hi * f / 1.1)} y2={y(hi * f / 1.1)} className="ggrid" />
              <text x={L - 10} y={y(hi * f / 1.1) + 4} textAnchor="end">{format(hi * f / 1.1)}</text>
            </g>
          ))}
          <g clipPath={`url(#${uid}-clip)`}>
            {live.map((s) => {
              const pts = [...s.points].sort((a, b) => a.day.localeCompare(b.day)).map((p) => [x(p.day), y(p.value)] as [number, number]);
              const d = monotonePath(pts);
              const first = pts[0] as [number, number], last = pts[pts.length - 1] as [number, number];
              return (
                <g key={s.key}>
                  <path d={`${d} L${last[0]},${y(0)} L${first[0]},${y(0)} Z`} fill={`url(#${uid}-${s.key})`} />
                  <path d={d} fill="none" style={{ stroke: s.color, filter: `drop-shadow(0 3px 6px color-mix(in srgb, ${s.color} 40%, transparent))` }} strokeWidth="2.6" strokeLinecap="round" />
                </g>
              );
            })}
          </g>
          <line x1={x(active)} x2={x(active)} y1={T} y2={H - B} className="gcross" />
          {live.map((s) => {
            const p = s.points.find((q) => q.day === active);
            return p ? <circle key={s.key} cx={x(p.day)} cy={y(p.value)} r="5.5" className="gring" style={{ fill: s.color }} /> : null;
          })}
          {xTicks.map((d, i) => <text key={d} x={x(d)} y={H - 8} textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}>{shortDay(d)}</text>)}
        </svg>
        <div className="gtip gtip-card" style={{ left: `clamp(90px, ${tipX}%, calc(100% - 90px))` }}>
          <small>{shortDay(active)}</small>
          {live.map((s) => {
            const p = s.points.find((q) => q.day === active);
            return <div key={s.key}><span><i className="gdot" style={{ background: s.color }} aria-hidden />{s.name}</span><b>{p ? format(p.value) : '—'}</b></div>;
          })}
        </div>
      </div>
      <div className="gsummary">
        {live.map((s) => {
          const vals = s.points.map((p) => p.value);
          const total = vals.reduce((a, b) => a + b, 0);
          return (
            <div key={s.key}>
              <span><i className="gdot" style={{ background: s.color }} aria-hidden />{s.name}</span>
              <b>{format(total)}</b>
              <small>total over {s.points.length} published days</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- donut */

export interface GSlice { key: string; label: string; value: number; color: string; sub?: string }

/** Glossy ring segments with gaps, a frosted centre, and a legend with shares. */
export function GlassDonut({ slices, centre, centreLabel }: { slices: GSlice[]; centre: string; centreLabel: string }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (!total) return <p className="empty">Nothing to divide.</p>;
  const R = 44, C = 2 * Math.PI * R, GAP = 1.6;
  let acc = 0;
  return (
    <div className="gdonut">
      <div className="gdonut-ring">
        <svg viewBox="0 0 110 110" role="img" aria-label={slices.map((s) => `${s.label} ${((s.value / total) * 100).toFixed(1)}%`).join(', ')}>
          <circle cx="55" cy="55" r={R} fill="none" className="gdonut-track" strokeWidth="14" />
          {slices.map((s) => {
            const len = (s.value / total) * C;
            const seg = <circle key={s.key} cx="55" cy="55" r={R} fill="none" strokeWidth="14" strokeLinecap="butt"
              style={{ stroke: s.color }} strokeDasharray={`${Math.max(0.5, len - GAP)} ${C}`} strokeDashoffset={-acc} className="gdonut-seg" />;
            acc += len;
            return seg;
          })}
        </svg>
        <div className="gdonut-centre"><b>{centre}</b><span>{centreLabel}</span></div>
      </div>
      <ul className="gdonut-legend">
        {slices.map((s) => (
          <li key={s.key}>
            <span><i className="gdot" style={{ background: s.color }} aria-hidden />{s.label}</span>
            <b>{((s.value / total) * 100).toFixed(1)}%</b>
            {s.sub && <small>{s.sub}</small>}
          </li>
        ))}
      </ul>
    </div>
  );
}
